import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllQueries, assignUsersToQueries, updateQueries, removeUsersFromQueries } from '../../api/queriesAPI';
import { getSingleOperation, updateFollowupDetails, addChangeRequest, addAdvancePayment } from '../../api/operationAPI';
import { getAllUsers } from '../../api/userAPI';
import * as TripAPI from '../../api/tripManagementAPI';

// ─── Data Normalizers ─────────────────────────────────────────────────────────

/**
 * Derive a human-readable trip status from travel_date + duration.
 * Queries only have lead_stage (Confirm/etc), not an explicit running/completed state.
 */
const deriveTripStatus = (travel_date, duration) => {
  if (!travel_date) return 'Upcoming';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(travel_date);
  const end = new Date(travel_date);
  end.setDate(end.getDate() + (parseInt(duration) || 1));
  if (start > today) return 'Upcoming';
  if (end <= today) return 'Completed';
  return 'Running';
};

/**
 * Derive payment status from cost and advance.
 */
const derivePaymentStatus = (cost, advance) => {
  if (!cost) return 'Pending';
  if (advance >= cost) return 'Paid';
  if (advance > 0) return 'Partial';
  return 'Pending';
};

/**
 * Derive hotel/driver confirmation from followup_details array (from Operation).
 * "Confirmed" if at least one entry has a non-empty hotel_confirmation value.
 */
const deriveHotelConfirmed = (followup_details = []) =>
  followup_details.some(f => f.hotel_confirmation && f.hotel_confirmation.trim() !== '');

const deriveDriverConfirmed = (followup_details = []) =>
  followup_details.some(f => f.driver && f.driver.trim() !== '');

const derivePermitDone = (followup_details = []) =>
  followup_details.some(f => f.permit && f.permit.trim() !== '');

/**
 * Map a raw Query document (from getAllQueries) into the unified Trip shape.
 *
 * When a query is confirmed, an Operation document is created with the
 * authoritative trip data (price, advance[], travel_date, pax, followup_details).
 * The getAllQueries aggregate now includes these as op_* fields.
 * We always prefer op_* over the Query's own fields for confirmed trips.
 */
export const normalizeQueryToTrip = (query) => {
  // ── Prefer Operation data (authoritative for confirmed trips) ──────────────
  const travelDate = query.op_travel_date || query.travel_date || null;
  const duration   = query.op_duration    || query.duration    || 0;

  // op_advance is an array of payment amounts; query.advance is the initial scalar
  let paidAmount = Array.isArray(query.op_advance) && query.op_advance.length > 0
    ? query.op_advance.reduce((sum, amt) => sum + (Number(amt) || 0), 0)
    : (Number(query.advance) || 0);

  const authoritativePrice = Number(query.op_price) || Number(query.cost) || 0;
  let totalAmount = authoritativePrice;
  let paymentDueDate = query.payment_due_date || null;
  let paymentStatus = derivePaymentStatus(totalAmount, paidAmount);

  // If PaymentSchedule exists, it overrides legacy payment fields
  if (query.payment_schedule) {
    const psTotal = Number(query.payment_schedule.total_amount) || 0;
    const psAdv = Number(query.payment_schedule.advance_payment) || 0;

    let instPaidSum = 0;
    let instTotalSum = 0;
    let earliestPendingDate = null;
    let isOverdue = false;
    const now = new Date();

    if (Array.isArray(query.payment_schedule.installments)) {
      query.payment_schedule.installments.forEach(inst => {
        const amt = Number(inst.amount) || 0;
        instTotalSum += amt;
        if (inst.status === 'Paid') {
          instPaidSum += amt;
        } else if (inst.status === 'Pending' && inst.due_date) {
          const instDate = new Date(inst.due_date);
          if (instDate < now) {
            isOverdue = true;
          }
          if (!earliestPendingDate || instDate < earliestPendingDate) {
            earliestPendingDate = instDate;
          }
        }
      });
    }

    // Determine consistent totalAmount:
    // If psTotal matches authoritativePrice OR psTotal is consistent with (psAdv + instTotalSum), use psTotal.
    // If psTotal is corrupted / out of sync with both, keep authoritativePrice.
    if (psTotal > 0) {
      if (totalAmount === 0 || Math.abs(psTotal - (psAdv + instTotalSum)) < 1 || Math.abs(psTotal - totalAmount) < 1) {
        totalAmount = psTotal;
      }
    }

    // Determine base advance payment:
    // Prefer psAdv if consistent with psTotal or if query advance is 0, else keep paidAmount (query.op_advance / advance)
    const baseAdvance = (psAdv > 0 && (Math.abs(psTotal - (psAdv + instTotalSum)) < 1 || paidAmount === 0))
      ? psAdv
      : paidAmount;

    paidAmount = baseAdvance + instPaidSum;

    if (paidAmount >= totalAmount && totalAmount > 0) {
      paymentStatus = 'Paid';
      paymentDueDate = null;
    } else if (isOverdue) {
      paymentStatus = 'Overdue';
      paymentDueDate = earliestPendingDate ? earliestPendingDate.toISOString() : null;
    } else {
      paymentStatus = paidAmount > 0 ? 'Partial' : 'Pending';
      paymentDueDate = earliestPendingDate ? earliestPendingDate.toISOString() : null;
    }
  }

  const adults   = Number(query.op_adults)   || 0;
  const children = Number(query.op_children) || 0;
  const totalPax = (adults + children) || Number(query.pax) || 0;

  // op_followup comes from Operation.followup_details — includes hotel/driver detail
  const followup = Array.isArray(query.op_followup) && query.op_followup.length > 0
    ? query.op_followup
    : (query.followup_details || []);

  const trip_status = deriveTripStatus(travelDate, duration);

  // ── Assignment — last manage_teams entry is the current executive ──────────
  const lastAssignment = Array.isArray(query.manage_teams) && query.manage_teams.length > 0
    ? query.manage_teams[query.manage_teams.length - 1]
    : null;

  return {
    _id:          query._id,
    booking_ref:  query._id,
    operation_id: query.operation_id || null,

    // Guest — from the Inquiry join in getAllQueries aggregate
    guest_id: {
      name:         query.guest_info?.guest_name         || '—',
      phone:        query.guest_info?.guest_phone        || '',
      email:        query.guest_info?.guest_email        || '',
      country_code: query.guest_info?.guest_country_code || '',
    },

    destination:  query.destination || '—',
    confirm_date: query.confirm_date || query.updated_at || query.created_at || query.createdAt || null,
    travel_date:  travelDate,
    return_date:  travelDate && duration
      ? new Date(new Date(travelDate).getTime() + parseInt(duration) * 86400000)
      : null,
    total_pax:    totalPax,
    duration,

    trip_status,

    allocation: (query.manage_teams || []).map(mt => ({
      user_id: {
        _id: mt.user_id?._id || mt.user_id,
        fullName: mt.user_id?.fullName || mt.fullName || 'Assigned'
      },
      date: mt.date,
      note: mt.status
    })),
    manage_teams: query.manage_teams || [],

    // Payment — calculated using PaymentSchedule if it exists, falling back to legacy fields
    payment: {
      total_amount: totalAmount,
      paid_amount:  paidAmount,
      due_amount:   Math.max(0, totalAmount - paidAmount),
      status:       paymentStatus,
      due_date:     paymentDueDate,
    },

    // Confirmations — derived from Operation's followup_details
    hotel_confirmed:  deriveHotelConfirmed(followup),
    driver_confirmed: deriveDriverConfirmed(followup),
    permit_done:      derivePermitDone(followup),

    followup_details: followup,
    _raw: query,
  };
};

// ─── KPI Computation ──────────────────────────────────────────────────────────

const computeManagerKPIs = (trips, currentUserId) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);

  const upcoming  = trips.filter(t => t.trip_status === 'Upcoming').length;
  const running   = trips.filter(t => t.trip_status === 'Running').length;
  const completed = trips.filter(t => t.trip_status === 'Completed').length;
  const cancelled = 0; // no cancelled lead_stage in current model

  const todaysGuests = trips
    .filter(t => {
      if (!t.travel_date) return false;
      const d = new Date(t.travel_date);
      return d >= today && d <= todayEnd;
    })
    .reduce((sum, t) => sum + (t.total_pax || 0), 0);

  const pendingPayments = trips.filter(t => t.payment.status !== 'Paid').length;
  const pendingHotels   = trips.filter(t => !t.hotel_confirmed  && t.trip_status !== 'Completed').length;
  const pendingDrivers  = trips.filter(t => !t.driver_confirmed && t.trip_status !== 'Completed').length;
  const urgentFollowUps = trips.filter(t => t.payment.status === 'Overdue' || !t.hotel_confirmed).length;

  return {
    total_trips:                   trips.length,
    upcoming,
    running,
    completed,
    cancelled,
    total_guests_today:            todaysGuests,
    pending_payment_collections:   pendingPayments,
    pending_hotel_confirmations:   pendingHotels,
    pending_driver_confirmations:  pendingDrivers,
    urgent_follow_ups:             urgentFollowUps,
  };
};

const computeExecutiveKPIs = (trips, currentUserId) => {
  const myTrips = trips.filter(t =>
    t.manage_teams?.some(m => {
      const uid = m.user_id?._id || m.user_id;
      return uid?.toString() === currentUserId?.toString();
    })
  );

  return {
    my_assigned_trips:    myTrips.length,
    upcoming:             myTrips.filter(t => t.trip_status === 'Upcoming').length,
    running:              myTrips.filter(t => t.trip_status === 'Running').length,
    completed:            myTrips.filter(t => t.trip_status === 'Completed').length,
    pending_confirmations: myTrips.filter(t => !t.hotel_confirmed || !t.driver_confirmed).length,
    pending_collections:   myTrips.filter(t => t.payment.status !== 'Paid').length,
  };
};

// ─── Async Thunks ──────────────────────────────────────────────────────────────

/**
 * Primary data loader — fetches Confirmed queries, normalizes to Trip shape.
 * The `status` filter (Upcoming/Running/Completed) is applied client-side
 * since the Query model doesn't store trip lifecycle status.
 */
export const loadAllTrips = createAsyncThunk(
  'tripMgmt/allTrips',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 200, status } = params;
      // Always fetch Confirmed queries only
      const response = await getAllQueries(page, limit, { lead_stage: 'Confirm' });
      const rawQueries = response?.queries || response?.data || [];
      const total      = response?.totalRows || rawQueries.length;

      let trips = rawQueries.map(normalizeQueryToTrip);

      // Client-side status filter
      if (status && status !== 'All') {
        trips = trips.filter(t => t.trip_status === status);
      }

      return { data: trips, pagination: { total, page, limit } };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Load KPIs — derives values from the full confirmed trips list.
 */
export const loadManagerKPIs = createAsyncThunk(
  'tripMgmt/managerKPIs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllQueries(1, 500, { lead_stage: 'Confirm' });
      const trips = (response?.queries || response?.data || []).map(normalizeQueryToTrip);
      return computeManagerKPIs(trips);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Load Executive KPIs — scoped to trips where the current user is in manage_teams.
 */
export const loadExecutiveKPIs = createAsyncThunk(
  'tripMgmt/executiveKPIs',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId   = getState().tokens?.user?._id;
      const response = await getAllQueries(1, 500, { lead_stage: 'Confirm' });
      const trips    = (response?.queries || response?.data || []).map(normalizeQueryToTrip);
      return computeExecutiveKPIs(trips, userId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Load a single trip — fetches the query's linked Operation for full detail.
 */
export const loadTripById = createAsyncThunk(
  'tripMgmt/tripById',
  async (id, { rejectWithValue }) => {
    try {
      // First try to find the query — we need to pass it through getAllQueries or a single fetch
      // Use the operation API to get operation details if operation_id is available
      const operation = await getSingleOperation(id).catch(() => null);
      return operation;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Running trips — filter from the confirmed list.
 */
export const loadRunningTrips = createAsyncThunk(
  'tripMgmt/runningTrips',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllQueries(1, 500, { lead_stage: 'Confirm' });
      const trips    = (response?.queries || response?.data || []).map(normalizeQueryToTrip);
      return trips
        .filter(t => t.trip_status === 'Running')
        .map(trip => {
          const hasNoHotel  = !trip.hotel_confirmed;
          const hasNoDriver = !trip.driver_confirmed;
          const color = (hasNoHotel || hasNoDriver) ? 'yellow' : 'green';
          return { ...trip, status_color: color };
        });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Calendar events — derived from confirmed trips.
 */
export const loadCalendarEvents = createAsyncThunk(
  'tripMgmt/calendar',
  async ({ start, end } = {}, { rejectWithValue }) => {
    try {
      const response = await getAllQueries(1, 500, { lead_stage: 'Confirm' });
      const trips    = (response?.queries || response?.data || []).map(normalizeQueryToTrip);

      const events = [];
      for (const trip of trips) {
        const name = trip.guest_id?.name || 'Guest';

        if (trip.travel_date) {
          events.push({
            id:       `${trip._id}-checkin`,
            title:    `✈ ${name} – Depart`,
            date:     trip.travel_date,
            type:     'arrival',
            color:    '#1976d2',
            trip_id:  trip._id,
          });
        }
        if (trip.return_date) {
          events.push({
            id:       `${trip._id}-return`,
            title:    `🏠 ${name} – Return`,
            date:     trip.return_date,
            type:     'departure',
            color:    '#388e3c',
            trip_id:  trip._id,
          });
        }
        if (trip.payment?.due_date) {
          events.push({
            id:       `${trip._id}-payment`,
            title:    `💰 ${name} – Payment Due`,
            date:     trip.payment.due_date,
            type:     'payment_due',
            color:    trip.payment.status === 'Overdue' ? '#d32f2f' : '#f57c00',
            trip_id:  trip._id,
          });
        }
      }
      return events;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Payment dashboard — filter confirmed trips by payment view.
 */
export const loadPaymentDashboard = createAsyncThunk(
  'tripMgmt/payments',
  async (view = 'all', { getState, rejectWithValue }) => {
    try {
      const userId     = getState().tokens?.user?._id;
      const permission = getState().tokens?.user?.permission;
      const response   = await getAllQueries(1, 500, { lead_stage: 'Confirm' });
      let trips        = (response?.queries || response?.data || []).map(normalizeQueryToTrip);

      // Scope to assigned trips for executives
      if (permission === 'Operation Executive' || permission === 'Operation') {
        trips = trips.filter(t =>
          t.manage_teams?.some(m => {
            const uid = m.user_id?._id || m.user_id;
            return uid?.toString() === userId?.toString();
          })
        );
      }

      const today   = new Date(); today.setHours(0, 0, 0, 0);
      const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);

      const unpaid = trips.filter(t => t.payment.status !== 'Paid');

      if (view === 'overdue') {
        return unpaid.filter(t => t.payment.due_date && new Date(t.payment.due_date) < today);
      }
      if (view === 'today') {
        return unpaid.filter(t => {
          if (!t.payment.due_date) return false;
          const d = new Date(t.payment.due_date);
          return d >= today && d <= new Date(today.getTime() + 86399999);
        });
      }
      if (view === 'week') {
        return unpaid.filter(t => {
          if (!t.payment.due_date) return false;
          const d = new Date(t.payment.due_date);
          return d >= today && d <= weekEnd;
        });
      }
      return unpaid;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Executive workload — all Operation Executive / Operation Manager users,
 * enriched with confirmed-trip counts from manage_teams.
 */
export const loadExecutiveWorkload = createAsyncThunk(
  'tripMgmt/workload',
  async (_, { rejectWithValue }) => {
    try {
      const OPS_PERMISSIONS = ['Operation', 'Operation Executive', 'Operation Manager'];

      const [usersRes, tripsRes] = await Promise.all([
        getAllUsers(),
        getAllQueries(1, 500, { lead_stage: 'Confirm' }),
      ]);

      // Backend returns a plain array; axios wraps it in .data
      const allUsers = Array.isArray(usersRes?.data) ? usersRes.data : [];
      const opsUsers = allUsers.filter(u => OPS_PERMISSIONS.includes(u.permission) && u.isActive !== false);

      // Build workload counts from manage_teams
      const trips = (tripsRes?.data || []).map(normalizeQueryToTrip);
      const counts = {};
      for (const trip of trips) {
        // Use only the last manage_teams entry (current assignment)
        const last = trip.manage_teams?.[trip.manage_teams.length - 1];
        if (!last) continue;
        const uid = (last.user_id?._id || last.user_id)?.toString();
        if (!uid) continue;
        if (!counts[uid]) counts[uid] = { total_trips: 0, running: 0, upcoming: 0, completed: 0 };
        counts[uid].total_trips++;
        if (trip.trip_status === 'Running')   counts[uid].running++;
        if (trip.trip_status === 'Upcoming')  counts[uid].upcoming++;
        if (trip.trip_status === 'Completed') counts[uid].completed++;
      }

      // Merge user list with counts (zero-counts for unassigned users)
      // Backend returns id (not _id) for users
      return opsUsers.map(u => {
        const uid = (u._id || u.id)?.toString();
        return {
          _id:         u._id || u.id,
          fullName:    u.fullName,
          email:       u.email,
          permission:  u.permission,
          total_trips: counts[uid]?.total_trips ?? 0,
          running:     counts[uid]?.running     ?? 0,
          upcoming:    counts[uid]?.upcoming    ?? 0,
          completed:   counts[uid]?.completed   ?? 0,
        };
      });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Trip activity timeline — uses the operation changes array.
 */
export const loadTripActivities = createAsyncThunk(
  'tripMgmt/activities',
  async (id, { rejectWithValue }) => {
    try {
      // id here is the operation_id (passed from trip._raw.operation_id)
      if (!id) return [];
      const operation = await getSingleOperation(id);
      // Map operation changes to the activity shape the UI expects
      return (operation?.changes || [])
        .slice()
        .reverse()
        .map(c => ({
          _id:           c._id,
          activity_type: c.type_changes === 'itinerary' ? 'Hotel Updated' : 'Note Added',
          description:   c.description,
          performed_by:  { fullName: c.user_id?.fullName || 'Team Member' },
          created_at:    c.date_time,
        }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Reminders — uses the TripManagement API (new layer).
 */
export const loadReminders = createAsyncThunk(
  'tripMgmt/reminders',
  async (_, { rejectWithValue }) => {
    try {
      return (await TripAPI.fetchReminders()).data;
    } catch {
      return []; // reminders are optional — don't break the dashboard
    }
  }
);

// ── Actions ───────────────────────────────────────────────────────────────────

/**
 * Assign a trip to an executive — updates manage_teams on the query.
 */
export const doAssignTrip = createAsyncThunk(
  'tripMgmt/assign',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      // id = query _id, data.executive_id (or array) = users to assign
      const userIds = data.executive_ids || [data.executive_id];
      const result = await assignUsersToQueries({
        query_ids: [id],
        user_ids:  userIds,
      });
      return { message: 'Assigned successfully', result };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const doRemoveTripAssignment = createAsyncThunk(
  'tripMgmt/removeAssign',
  async ({ id, executive_ids }, { rejectWithValue }) => {
    try {
      const result = await removeUsersFromQueries({
        query_ids: [id],
        user_ids: executive_ids,
      });
      return { message: 'Removed successfully', result };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Bulk assign trips to one executive.
 */
export const doBulkAssign = createAsyncThunk(
  'tripMgmt/bulkAssign',
  async ({ trip_ids, executive_ids }, { rejectWithValue }) => {
    try {
      const result = await assignUsersToQueries({ query_ids: trip_ids, user_ids: executive_ids });
      return { message: `${trip_ids.length} trips assigned`, result };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Update trip status — stored on the query as a special remark tag since
 * the query model doesn't have an explicit trip_status field.
 * We use `updateQueries` to patch a lightweight status field.
 */
export const doUpdateStatus = createAsyncThunk(
  'tripMgmt/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      await updateQueries(id, { trip_status_override: status });
      return { id, status };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Update hotel / driver confirmations — patches the Operation's followup_details.
 * Requires the operation_id from the trip.
 */
export const doUpdateConfirmations = createAsyncThunk(
  'tripMgmt/confirmations',
  async ({ id, operationId, data }, { rejectWithValue }) => {
    try {
      if (!operationId) throw new Error('No operation linked to this trip');
      // Update all followup entries with the confirmation status
      const operation = await getSingleOperation(operationId);
      const updated = (operation?.followup_details || []).map(f => ({
        ...f,
        hotel_confirmation: data.hotel_confirmed !== undefined
          ? (data.hotel_confirmed ? 'Confirmed' : '')
          : f.hotel_confirmation,
        driver: data.driver_confirmed !== undefined
          ? (data.driver_confirmed ? f.driver || 'Confirmed' : '')
          : f.driver,
        permit: data.permit_done !== undefined
          ? (data.permit_done ? f.permit || 'Done' : '')
          : f.permit,
      }));
      const result = await updateFollowupDetails(operationId, { followup_details: updated });
      return { message: 'Confirmations updated', ...data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Add a note — stored as a change request on the Operation.
 */
export const doAddNote = createAsyncThunk(
  'tripMgmt/addNote',
  async ({ id, operationId, data }, { rejectWithValue }) => {
    try {
      if (!operationId) throw new Error('No operation linked to this trip');
      const result = await addChangeRequest(operationId, { description: data.content });
      return { message: 'Note added', result };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Payment collection — adds a new instalment to Operation.trip_details.advance[].
 * Uses the operationId (from the normalized trip) for the Operation update,
 * and also records a note via addChangeRequest so the activity timeline reflects it.
 */
export const doAddCollectionNote = createAsyncThunk(
  'tripMgmt/collectPayment',
  async ({ id, operationId, data }, { rejectWithValue }) => {
    try {
      if (!operationId) throw new Error('No operation linked to this trip');
      const { collected, note } = data; // collected = new instalment amount

      // Push the new payment instalment onto the Operation's advance array
      await addAdvancePayment(operationId, { amount: collected, note });

      // Also log it as a change request for the activity timeline
      if (note) {
        await addChangeRequest(operationId, {
          description: `Payment collected: ₹${collected?.toLocaleString() || 0}${note ? ` — ${note}` : ''}`,
        });
      }

      return { message: 'Payment recorded', collected };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Raise escalation — uses TripManagement API (new layer) as the query/operation
 * models don't have an escalation concept.
 */
export const doRaiseEscalation = createAsyncThunk(
  'tripMgmt/raiseEscalation',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await TripAPI.raiseEscalation(id, data);
      return { message: 'Escalation raised', ...result.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const doResolveEscalation = createAsyncThunk(
  'tripMgmt/resolveEscalation',
  async ({ id, escId, data }, { rejectWithValue }) => {
    try {
      const result = await TripAPI.resolveEscalation(id, escId, data);
      return { message: 'Escalation resolved', ...result.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/**
 * Checklist — uses TripManagement API.
 */
export const doUpdateChecklist = createAsyncThunk(
  'tripMgmt/checklist',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await TripAPI.updateChecklist(id, data);
      return result.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const doAddChecklistItem = createAsyncThunk(
  'tripMgmt/addChecklistItem',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await TripAPI.addChecklistItem(id, data);
      return result.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const doUpdateFeedback = createAsyncThunk(
  'tripMgmt/feedback',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await TripAPI.updateGuestFeedback(id, data);
      return result.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  managerKPIs:       null,
  executiveKPIs:     null,
  trips:             { data: [], pagination: {} },
  runningTrips:      [],
  selectedTrip:      null,
  executiveWorkload: [],
  calendarEvents:    [],
  payments:          [],
  activities:        [],
  reminders:         [],

  loading: {
    kpis: false, trips: false, running: false, workload: false,
    calendar: false, payments: false, activities: false, trip: false,
    reminders: false, action: false,
  },
  errors: {},
  filters: { status: 'All', page: 1, limit: 200 },
  selectedTripIds: [],
};

const pending  = (key) => (state) => { state.loading[key] = true; delete state.errors[key]; };
const rejected = (key) => (state, action) => { state.loading[key] = false; state.errors[key] = action.payload; };
const noop     = () => {};

const tripManagementSlice = createSlice({
  name: 'tripManagement',
  initialState,
  reducers: {
    setFilter:         (state, { payload }) => { state.filters = { ...state.filters, ...payload }; },
    setSelectedTrips:  (state, { payload }) => { state.selectedTripIds = payload; },
    clearSelectedTrip: (state)              => { state.selectedTrip = null; },
    clearErrors:       (state)              => { state.errors = {}; },
    // Optimistically update a single trip's confirmation flags in the list
    updateTripInList:  (state, { payload: { id, changes } }) => {
      const idx = state.trips.data.findIndex(t => t._id?.toString() === id?.toString());
      if (idx !== -1) state.trips.data[idx] = { ...state.trips.data[idx], ...changes };
      if (state.runningTrips) {
        const ri = state.runningTrips.findIndex(t => t._id?.toString() === id?.toString());
        if (ri !== -1) state.runningTrips[ri] = { ...state.runningTrips[ri], ...changes };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadManagerKPIs.pending,   pending('kpis'))
      .addCase(loadManagerKPIs.rejected,  rejected('kpis'))
      .addCase(loadManagerKPIs.fulfilled, (s, { payload }) => { s.loading.kpis = false; s.managerKPIs = payload; })

      .addCase(loadExecutiveKPIs.pending,   pending('kpis'))
      .addCase(loadExecutiveKPIs.rejected,  rejected('kpis'))
      .addCase(loadExecutiveKPIs.fulfilled, (s, { payload }) => { s.loading.kpis = false; s.executiveKPIs = payload; })

      .addCase(loadAllTrips.pending,   pending('trips'))
      .addCase(loadAllTrips.rejected,  rejected('trips'))
      .addCase(loadAllTrips.fulfilled, (s, { payload }) => { s.loading.trips = false; s.trips = payload; })

      .addCase(loadTripById.pending,   pending('trip'))
      .addCase(loadTripById.rejected,  rejected('trip'))
      .addCase(loadTripById.fulfilled, (s, { payload }) => { s.loading.trip = false; s.selectedTrip = payload; })

      .addCase(loadRunningTrips.pending,   pending('running'))
      .addCase(loadRunningTrips.rejected,  rejected('running'))
      .addCase(loadRunningTrips.fulfilled, (s, { payload }) => { s.loading.running = false; s.runningTrips = payload; })

      .addCase(loadExecutiveWorkload.pending,   pending('workload'))
      .addCase(loadExecutiveWorkload.rejected,  rejected('workload'))
      .addCase(loadExecutiveWorkload.fulfilled, (s, { payload }) => { s.loading.workload = false; s.executiveWorkload = payload; })

      .addCase(loadCalendarEvents.pending,   pending('calendar'))
      .addCase(loadCalendarEvents.rejected,  rejected('calendar'))
      .addCase(loadCalendarEvents.fulfilled, (s, { payload }) => { s.loading.calendar = false; s.calendarEvents = payload; })

      .addCase(loadPaymentDashboard.pending,   pending('payments'))
      .addCase(loadPaymentDashboard.rejected,  rejected('payments'))
      .addCase(loadPaymentDashboard.fulfilled, (s, { payload }) => { s.loading.payments = false; s.payments = payload; })

      .addCase(loadTripActivities.pending,   pending('activities'))
      .addCase(loadTripActivities.rejected,  rejected('activities'))
      .addCase(loadTripActivities.fulfilled, (s, { payload }) => { s.loading.activities = false; s.activities = payload; })

      .addCase(loadReminders.pending,   pending('reminders'))
      .addCase(loadReminders.rejected,  rejected('reminders'))
      .addCase(loadReminders.fulfilled, (s, { payload }) => { s.loading.reminders = false; s.reminders = payload; })

      // Action thunks — just clear the loading flag; callers re-fetch after success
      .addCase(doAssignTrip.pending,          pending('action'))
      .addCase(doAssignTrip.rejected,         rejected('action'))
      .addCase(doAssignTrip.fulfilled,        (s) => { s.loading.action = false; })

      .addCase(doBulkAssign.pending,          pending('action'))
      .addCase(doBulkAssign.rejected,         rejected('action'))
      .addCase(doBulkAssign.fulfilled,        (s) => { s.loading.action = false; })

      .addCase(doUpdateStatus.pending,        pending('action'))
      .addCase(doUpdateStatus.rejected,       rejected('action'))
      .addCase(doUpdateStatus.fulfilled,      (s) => { s.loading.action = false; })

      .addCase(doUpdateConfirmations.pending,  pending('action'))
      .addCase(doUpdateConfirmations.rejected, rejected('action'))
      .addCase(doUpdateConfirmations.fulfilled,(s) => { s.loading.action = false; })

      .addCase(doAddNote.pending,             pending('action'))
      .addCase(doAddNote.rejected,            rejected('action'))
      .addCase(doAddNote.fulfilled,           (s) => { s.loading.action = false; })

      .addCase(doAddCollectionNote.pending,   pending('action'))
      .addCase(doAddCollectionNote.rejected,  rejected('action'))
      .addCase(doAddCollectionNote.fulfilled, (s) => { s.loading.action = false; })

      .addCase(doRaiseEscalation.pending,     pending('action'))
      .addCase(doRaiseEscalation.rejected,    rejected('action'))
      .addCase(doRaiseEscalation.fulfilled,   (s) => { s.loading.action = false; })

      .addCase(doResolveEscalation.pending,   pending('action'))
      .addCase(doResolveEscalation.rejected,  rejected('action'))
      .addCase(doResolveEscalation.fulfilled, (s) => { s.loading.action = false; })

      .addCase(doUpdateChecklist.pending,     pending('action'))
      .addCase(doUpdateChecklist.rejected,    rejected('action'))
      .addCase(doUpdateChecklist.fulfilled,   (s) => { s.loading.action = false; })

      .addCase(doAddChecklistItem.pending,    pending('action'))
      .addCase(doAddChecklistItem.rejected,   rejected('action'))
      .addCase(doAddChecklistItem.fulfilled,  (s) => { s.loading.action = false; })

      .addCase(doUpdateFeedback.pending,      pending('action'))
      .addCase(doUpdateFeedback.rejected,     rejected('action'))
      .addCase(doUpdateFeedback.fulfilled,    (s) => { s.loading.action = false; });
  },
});

export const { setFilter, setSelectedTrips, clearSelectedTrip, clearErrors, updateTripInList } =
  tripManagementSlice.actions;

export default tripManagementSlice.reducer;
